import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Stack,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PersonIcon from '@mui/icons-material/Person';
import BookIcon from '@mui/icons-material/Book';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNexaStayThemeContext } from './NexaStayThemeContext';
import { useNexaStayBasePath, nexastayPath, resolveNexaStayHotelId } from './useNexaStayBasePath';
import NexaStayLoginDialog from './NexaStayLoginDialog';
import NexaStayRegisterDialog from './NexaStayRegisterDialog';
import { getGuestStorageKey } from './nexastayApi';

const NAV_LINKS = [
  { to: (base, id) => nexastayPath(base, id), label: 'Home', icon: HomeIcon },
  { to: (base, id) => nexastayPath(base, id, 'about'), label: 'About', icon: MeetingRoomIcon },
  { to: (base, id) => nexastayPath(base, id, 'contact'), label: 'Contact', icon: ContactMailIcon },
];

export default function NexaStayHeader() {
  const { hotelId: routeHotelId } = useParams();
  const hotelId = resolveNexaStayHotelId(routeHotelId);
  const basePath = useNexaStayBasePath();
  const navigate = useNavigate();
  const { theme, hotel } = useNexaStayThemeContext();
  const accent = theme.accent;
  const location = useLocation();
  const isActive = (path) => location.pathname === path;
  const headerBg = theme.headerFooterBg ?? theme.paper;
  const headerShadow = theme.mode === 'dark'
    ? '0 4px 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.04)'
    : '0 4px 24px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)';

  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [guestId, setGuestId] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    if (!hotelId) return;
    try {
      setGuestId(localStorage.getItem(getGuestStorageKey(hotelId)));
    } catch (_) {
      setGuestId(null);
    }
    // Re-run when pathname/search change so we pick up guest_id after Google OAuth redirect
  }, [hotelId, location.pathname, location.search]);

  const isLoggedIn = !!guestId;

  const handleLogout = () => {
    if (hotelId) {
      try {
        localStorage.removeItem(getGuestStorageKey(hotelId));
      } catch (_) {}
    }
    setGuestId(null);
    setProfileAnchor(null);
    setMobileDrawerOpen(false);
    navigate(nexastayPath(basePath, hotelId));
  };

  const closeMobileDrawer = () => setMobileDrawerOpen(false);

  const navLinkSx = (active) => ({
    textTransform: 'none',
    fontWeight: active ? 600 : 500,
    fontSize: '0.9375rem',
    minHeight: 40,
    px: 1.75,
    borderRadius: 2.5,
    color: active ? accent : theme.textSecondary,
    position: 'relative',
    transition: 'color 0.2s, background-color 0.2s, box-shadow 0.2s',
    '&:hover': {
      color: accent,
      bgcolor: `${accent}14`,
      boxShadow: `0 0 0 1px ${accent}30`,
    },
    ...(active && {
      bgcolor: `${accent}12`,
      boxShadow: `0 0 0 1px ${accent}40`,
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 6,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 20,
        height: 2,
        borderRadius: 1,
        bgcolor: accent,
      },
    }),
  });

  const navContent = (
    <>
      {NAV_LINKS.map(({ to, label, icon: Icon }) => (
        <Button
          key={label}
          component={Link}
          to={to(basePath, hotelId)}
          startIcon={<Icon sx={{ fontSize: 20, opacity: isActive(to(basePath, hotelId)) ? 1 : 0.8 }} />}
          size="medium"
          sx={{
            ...navLinkSx(isActive(to(basePath, hotelId))),
            minWidth: 0,
          }}
        >
          {label}
        </Button>
      ))}
      <Divider orientation="vertical" flexItem sx={{ borderColor: theme.borderAccent, height: 24, alignSelf: 'center', mx: 0.5 }} />
      {isLoggedIn ? (
        <>
          <Button
            onClick={(e) => setProfileAnchor(e.currentTarget)}
            endIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
            startIcon={<PersonIcon sx={{ fontSize: 20 }} />}
            size="medium"
            sx={{
              ...navLinkSx(false),
              minWidth: 0,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Profile
          </Button>
          <Menu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={() => setProfileAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  borderRadius: 2,
                  boxShadow: theme.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.12)',
                  border: `1px solid ${theme.borderAccent}`,
                  bgcolor: theme.paper,
                },
              },
            }}
          >
            <MenuItem
              component={Link}
              to={nexastayPath(basePath, hotelId, 'profile')}
              onClick={() => setProfileAnchor(null)}
              sx={{ py: 1.25 }}
            >
              <ListItemIcon>
                <PersonOutlineIcon sx={{ fontSize: 22, color: accent }} />
              </ListItemIcon>
              <ListItemText primary="My profile" primaryTypographyProps={{ fontWeight: 500 }} />
            </MenuItem>
            <MenuItem
              component={Link}
              to={nexastayPath(basePath, hotelId, 'bookings')}
              onClick={() => setProfileAnchor(null)}
              sx={{ py: 1.25 }}
            >
              <ListItemIcon>
                <BookIcon sx={{ fontSize: 22, color: accent }} />
              </ListItemIcon>
              <ListItemText primary="My Booking" primaryTypographyProps={{ fontWeight: 500 }} />
            </MenuItem>
            <Divider sx={{ borderColor: theme.border, my: 0.5 }} />
            <MenuItem onClick={handleLogout} sx={{ py: 1.25 }}>
              <ListItemIcon>
                <LogoutIcon sx={{ fontSize: 22, color: theme.textSecondary }} />
              </ListItemIcon>
              <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 500 }} />
            </MenuItem>
          </Menu>
        </>
      ) : (
        <>
          <Button
            onClick={() => setLoginOpen(true)}
            startIcon={<LoginIcon sx={{ fontSize: 20 }} />}
            size="medium"
            sx={{ ...navLinkSx(false), minWidth: 0 }}
          >
            Log In
          </Button>
          <Button
            onClick={() => setRegisterOpen(true)}
            variant="contained"
            startIcon={<PersonAddIcon sx={{ fontSize: 20 }} />}
            size="medium"
            sx={{
              bgcolor: accent,
              color: theme.mode === 'dark' ? '#121212' : '#000',
              minWidth: 0,
              minHeight: 40,
              borderRadius: 2.5,
              px: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9375rem',
              boxShadow: `0 2px 8px ${accent}45`,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                bgcolor: accent,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 14px ${accent}55`,
              },
            }}
          >
            Register
          </Button>
        </>
      )}
    </>
  );

  return (
    <Box
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        height: { xs: 56, sm: 64 },
        minHeight: { xs: 56, sm: 64 },
        display: 'flex',
        alignItems: 'center',
        backdropFilter: 'blur(16px) saturate(1.2)',
        WebkitBackdropFilter: 'blur(16px) saturate(1.2)',
        borderBottom: `1px solid ${theme.borderAccent}`,
        boxShadow: headerShadow,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: headerBg,
          pointerEvents: 'none',
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 1400, mx: 'auto', px: { xs: 1.5, sm: 2 }, py: 0, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box component={Link} to={nexastayPath(basePath, hotelId)} sx={{ textDecoration: 'none', color: theme.textPrimary, display: 'flex', alignItems: 'center', transition: 'color 0.2s, opacity 0.2s', '&:hover': { color: accent, opacity: 0.95 } }}>
          {(hotel?.logo_url || hotel?.image_url || hotel?.images?.[0]) ? (
            <Box
              component="img"
              src={hotel.logo_url || hotel.image_url || hotel.images[0]}
              alt={hotel?.name}
              sx={{
                maxHeight: { xs: 40, sm: 48 },
                maxWidth: 200,
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
                verticalAlign: 'middle',
              }}
            />
          ) : (
            <Box
              sx={{
                width: { xs: 40, sm: 44 },
                height: { xs: 40, sm: 44 },
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${accent}30, ${accent}18)`,
                color: accent,
                fontWeight: 700,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                border: `1px solid ${theme.borderAccent}`,
              }}
            >
              {hotel?.name?.slice(0, 2).toUpperCase() || 'H'}
            </Box>
          )}
        </Box>

        {/* Desktop nav – hidden on small screens */}
        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ display: { xs: 'none', md: 'flex' } }}>
          {navContent}
        </Stack>

        {/* Mobile: menu icon opens drawer, close icon closes it */}
        <IconButton
          aria-label={mobileDrawerOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMobileDrawerOpen((prev) => !prev)}
          sx={{
            display: { xs: 'inline-flex', md: 'none' },
            color: theme.textPrimary,
            '&:hover': { bgcolor: `${accent}18`, color: accent },
          }}
        >
          {mobileDrawerOpen ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      {/* Right drawer for mobile menu – half screen on small/mobile */}
      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={closeMobileDrawer}
        slotProps={{
          backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.4)' } },
        }}
        PaperProps={{
          sx: {
            width: '50vw !important',
            maxWidth: 320,
            minWidth: 240,
            bgcolor: theme.paper,
            borderLeft: `1px solid ${theme.borderAccent}`,
            boxShadow: theme.mode === 'dark' ? '0 0 40px rgba(0,0,0,0.5)' : '0 0 24px rgba(0,0,0,0.12)',
          },
        }}
      >
        <Box sx={{ py: 2, px: 0 }}>
          <Typography variant="subtitle1" sx={{ px: 2, pb: 1, color: theme.textSecondary, fontWeight: 600 }}>
            Menu
          </Typography>
          <List disablePadding>
            {NAV_LINKS.map(({ to, label, icon: Icon }) => (
              <ListItemButton
                key={label}
                component={Link}
                to={to(basePath, hotelId)}
                onClick={closeMobileDrawer}
                selected={isActive(to(basePath, hotelId))}
                sx={{
                  py: 1.5,
                  px: 2,
                  color: isActive(to(basePath, hotelId)) ? accent : theme.textPrimary,
                  '&.Mui-selected': { bgcolor: `${accent}14`, borderLeft: 3, borderColor: accent },
                  '&:hover': { bgcolor: `${accent}0c` },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                  <Icon />
                </ListItemIcon>
                <ListItemText primary={label} primaryTypographyProps={{ fontWeight: 500 }} />
              </ListItemButton>
            ))}
          </List>
          <Divider sx={{ borderColor: theme.borderAccent, my: 1 }} />
          {isLoggedIn ? (
            <List disablePadding>
              <ListItemButton
                component={Link}
                to={nexastayPath(basePath, hotelId, 'profile')}
                onClick={closeMobileDrawer}
                sx={{ py: 1.5, px: 2, color: theme.textPrimary, '&:hover': { bgcolor: `${accent}0c` } }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}><PersonOutlineIcon /></ListItemIcon>
                <ListItemText primary="Profile" primaryTypographyProps={{ fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton
                component={Link}
                to={nexastayPath(basePath, hotelId, 'bookings')}
                onClick={closeMobileDrawer}
                sx={{ py: 1.5, px: 2, color: theme.textPrimary, '&:hover': { bgcolor: `${accent}0c` } }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}><BookIcon /></ListItemIcon>
                <ListItemText primary="My Bookings" primaryTypographyProps={{ fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton
                onClick={() => { closeMobileDrawer(); handleLogout(); }}
                sx={{ py: 1.5, px: 2, color: theme.textPrimary, '&:hover': { bgcolor: `${accent}0c` } }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}><LogoutIcon /></ListItemIcon>
                <ListItemText primary="Log out" primaryTypographyProps={{ fontWeight: 500 }} />
              </ListItemButton>
            </List>
          ) : (
            <List disablePadding>
              <ListItemButton
                onClick={() => { closeMobileDrawer(); setLoginOpen(true); }}
                sx={{ py: 1.5, px: 2, color: theme.textPrimary, '&:hover': { bgcolor: `${accent}0c` } }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}><LoginIcon /></ListItemIcon>
                <ListItemText primary="Log In" primaryTypographyProps={{ fontWeight: 500 }} />
              </ListItemButton>
              <ListItemButton
                onClick={() => { closeMobileDrawer(); setRegisterOpen(true); }}
                sx={{
                  py: 1.5,
                  px: 2,
                  mx: 2,
                  mt: 1,
                  borderRadius: 2,
                  bgcolor: accent,
                  color: theme.mode === 'dark' ? '#121212' : '#000',
                  '&:hover': { bgcolor: accent, opacity: 0.92 },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}><PersonAddIcon /></ListItemIcon>
                <ListItemText primary="Register" primaryTypographyProps={{ fontWeight: 600 }} />
              </ListItemButton>
            </List>
          )}
        </Box>
      </Drawer>

      <NexaStayLoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        hotelId={hotelId}
        onLoginSuccess={(id) => {
          setGuestId(id);
          setLoginOpen(false);
        }}
      />
      <NexaStayRegisterDialog
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onSignInClick={() => { setRegisterOpen(false); setLoginOpen(true); }}
        hotelId={hotelId}
        onRegisterSuccess={(data) => {
          if (data?.guest_id && hotelId) {
            try {
              localStorage.setItem(getGuestStorageKey(hotelId), String(data.guest_id));
            } catch (_) {}
            setGuestId(String(data.guest_id));
            setRegisterOpen(false);
            navigate(nexastayPath(basePath, hotelId, 'profile'));
          }
        }}
      />
    </Box>
  );
}
